import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import Loadable from 'components/Loadable';
import SimpleLayout from 'layout/Simple';
import DashboardLayout from 'layout/Dashboard';
import { SimpleLayoutType } from 'config';
import UserDashboardLayout from '../layout/UserDashboard';
import BillingResult from 'billing/Result';
import BillingReturn from '../billing/BillingReturn';

// auth wrappers (user)
import RequireAuth from './RequireAuth';
import AlreadyAuthed from './AlreadyAuthed';

import OAuthRelay from 'login/OAuthRelay';

// admin wrappers (separate token)
import RequireAdminAuth from './RequireAdminAuth';
import AlreadyAuthedAdmin from './AlreadyAuthedAdmin';

// pages (reuse or split admin/user pages as needed)
const LoginPage = Loadable(lazy(() => import('login/index')));
const DashboardPage = Loadable(lazy(() => import('Dashboard/dashboard')));
const Subscriptions = Loadable(lazy(() => import('../subscriptions')));
const Addons = Loadable(lazy(() => import('../subscriptions/addons')));
const FacebookUsers = Loadable(lazy(() => import('../facebook-users')));
const Invoices = Loadable(lazy(() => import('../invoice')));
const AddOnInvoices = Loadable(lazy(() => import('../invoice/addonsInvoice')));
const UserLogin = Loadable(lazy(() => import('login/UserFacebookLogin')));
const UserDashboard = Loadable(lazy(() => import('Dashboard/dashboard')));
const BulkUsers = Loadable(lazy(() => import('../Bulk list')));
const AdminProfileView = Loadable(
  lazy(() => import('profile/AdminProfileView'))
);
const UserProfile = Loadable(lazy(() => import('profile/Userprofile'))); // src/userprofile/index.jsx
const FacebookPages = Loadable(lazy(() => import('facebook/pages'))); // src/facebook/pages/index.jsx
const PageView = Loadable(lazy(() => import('facebook/pages/view/')));
const BillingSuccess = Loadable(lazy(() => import('billing/Success')));
const BillingCancel = Loadable(lazy(() => import('billing/Cancel')));


// (optional) admin-specific pages; for now reuse the same ones
const AdminLoginPage = LoginPage;
const AdminDashboardPage = DashboardPage;

const router = createBrowserRouter(
  [
    // root -> /login (user)
    { index: true, element: <Navigate to="/login" replace /> },
    { path: '/auth/facebook/callback', element: <OAuthRelay /> },
    { path: '/auth/facebook/callback/', element: <OAuthRelay /> },
    { path: '/oauth-relay', element: <OAuthRelay /> },
    { path: '/oauth-relay/', element: <OAuthRelay /> },

    // ================= USER APP =================
    {
      path: '/login',
      element: (
        <AlreadyAuthed>
          <SimpleLayout layout={SimpleLayoutType.SIMPLE} />
        </AlreadyAuthed>
      ),
      children: [{ index: true, element: <UserLogin /> }]
    },
    {
      path: '/',
      element: (
        <RequireAuth>
          <UserDashboardLayout />
        </RequireAuth>
      ),
      children: [
        { path: 'dashboard', element: <FacebookPages /> },
        { path: 'profile', element: <UserProfile /> },
        { path: 'pages/:pageId', element: <PageView /> },
        { path: 'billing/success', element: <BillingResult mode="success" /> },
        { path: 'billing/cancel', element: <BillingResult mode="cancel" /> },
        { path: 'billing/return', element: <BillingReturn />},

      ]
    },

    // ================= ADMIN APP =================
    // /admin -> /admin/login

    {
      path: '/admin',
      children: [
        {
          path: 'login',
          element: (
            <AlreadyAuthedAdmin>
              <SimpleLayout layout={SimpleLayoutType.SIMPLE} />
            </AlreadyAuthedAdmin>
          ),
          children: [{ index: true, element: <LoginPage /> }]
        },
        {
          path: '',
          element: (
            <RequireAdminAuth>
              <DashboardLayout />
            </RequireAdminAuth>
          ),
          children: [
            { index: true, element: <Navigate to="dashboard" replace /> },
            { path: 'dashboard', element: <DashboardPage /> },
            { path: 'subscriptions/manage', element: <Subscriptions /> },
            { path: 'subscriptions/addons', element: <Addons /> },
            { path: 'facebook-users', element: <FacebookUsers /> },
            { path: 'invoices/subscription-invoices', element: <Invoices /> },
            { path: 'invoices/addons-invoices', element: <AddOnInvoices /> },
            { path: 'profile', element: <AdminProfileView /> },
            { path: 'bulk-jobs', element: <BulkUsers /> },
          ]
        }
      ]
    }
  ],
  { basename: import.meta.env.VITE_APP_BASE_NAME } // '/zingoblast'
);

export default router;
